import { withoutChartScenesPointer } from 'js/src/agent/skills/definitions/chartScenes';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';

import {
    EMPTY_SKETCH_AGENT_CONTEXT,
    getSketchUserPreferences,
    loadSketchAgentContext,
    type SketchAgentContextData,
    type SketchUserPreferencesOptions,
} from '../sketch-context/sketchAgentContext';
import { getToolboxPromptSections } from '../sketch-context/toolboxPromptSections';
import { CreateBlankVideoTool } from '../tools/CreateBlankVideoTool';
import { RebuildVideoTool } from '../tools/RebuildVideoTool';
import { SketchResearchTool } from '../tools/sketch/SketchResearchTool';
import { UrlFetchTool } from '../tools/sketch/UrlFetchTool';
import { SuggestAnimatedExplainerTool } from '../tools/sketch/SuggestAnimatedExplainerTool';
import { SuggestUpgradeTool } from '../tools/sketch/SuggestUpgradeTool';
import type { Tool } from '../tools/Tool';
import { getCurrentDate, getKnowledgeCutoff } from '../types';
import { getHowOurVideosWorkGuide } from '../video-state/serialization/formatting';

import { RootAgent, type RootAgentConfig } from './RootAgent';

import { getVideoAnnotationGuide } from 'js/src/agent';
import { getActiveSkills } from 'js/src/agent/skills/skillStore';
import type { AnySkill, SkillSurface } from 'js/src/agent/skills/types';
import {
    AddAIAvatarSceneGroupTool,
    AddAIVoiceoverSceneGroupTool,
    AddRecordedVoiceoverSceneGroupTool,
    AddSceneTool,
    AddSubsceneTool,
    AddTalkingHeadSceneGroupTool,
    AddVoiceoverSceneTool,
    AnalyzeClipFramingTool,
    AnalyzeVideoTool,
    AskUserQuestionTool,
    DamMediaSearchTool,
    DeleteSceneGroupTool,
    DocumentExtractTool,
    DeleteSceneTool,
    EditAIVoiceoverTool,
    FixPronunciationTool,
    EditMediaTool,
    FetchReferenceVideoTool,
    FindWaveformSceneDesignTool,
    GenerateAIMediaTool,
    GetVideoStateTool,
    IconSearchTool,
    ImageAskTool,
    LintVideoTool,
    ListMediaTool,
    ListSceneDesignTool,
    MediaSearchTool,
    MoveSceneGroupTool,
    MusicSearchTool,
    FindTemplatesTool,
    ListAvatarsTool,
    ReviewSceneImagesTool,
    RunSubagentTool,
    SearchUploadedVoiceoversTool,
    SearchVoicesTool,
    SetAspectRatioTool,
    SetAssetGroupTool,
    SetAssetColorTool,
    SetCaptionsTool,
    SetColorPaletteTool,
    SetGlobalColorGradingTool,
    SetMediaEffectsTool,
    SetTemplateColorTool,
    SetIconTool,
    SetSceneTransitionTool,
    SetMediaTool,
    SetMusicTool,
    SetMusicVolumeTool,
    SetSceneGroupVolumeTool,
    SetSceneDurationTool,
    SetSceneRangeTool,
    SetTextAnimationTool,
    SetTextColorTool,
    SetTextPositionPresetTool,
    SetTextTool,
    SetWatermarkTool,
    SetWaveformStyleTool,
    SplitSceneGroupTool,
    TranslateVideoTool,
    TrimSceneGroupTool,
    CleanUpRecordingTool,
    ProposeClipsTool,
} from 'js/src/agent/tools';
import { buildDesignAuthoringTools } from 'js/src/agent/html-design/htmlDesignTools';
import { getHtmlDesignSkills } from 'js/src/agent/html-design/skills/skillRegistry';
import { ApplyBrandKitTool } from 'js/src/agent/tools/brand-kit/ApplyBrandKitTool';
import { ExtractBrandTool } from 'js/src/agent/tools/brand-kit/ExtractBrandTool';
import { ListBrandKitsTool } from 'js/src/agent/tools/brand-kit/ListBrandKitsTool';
import { ToolboxSaveBrandKitTool } from 'js/src/agent/tools/brand-kit/SaveBrandKitTool';
import { LoadSkillTool } from 'js/src/agent/tools/LoadSkillTool';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { getAllowedSubagentTypes } from 'js/src/agent/subagents/subagentTypes';
import type { SubagentType } from 'js/src/agent/subagents/subagentTypes';
import { getChartScenePrompt } from 'js/src/agent/html-design/chart/chartScenePrompt';
import { WriteSceneHtmlTool } from 'js/src/agent/html-design/chart/WriteSceneHtmlTool';
import {
    DEFAULT_MAX_CONCURRENT_SUBAGENTS,
    DEFAULT_MAX_SUBAGENTS_PER_MESSAGE,
    SubagentRegistry,
} from 'js/src/agent/subagents/SubagentRegistry';
import {
    getCanPublishAIAvatar,
    getCanUseAIAvatar,
    getCanUseAIVoiceover,
    getIsUserAllowedAccessToColorPalettes,
} from 'js/src/data/account';
import { getVideoId, setToolboxCreatingVideo, setVideoNameOperation } from 'js/src/data/app';
import Flags from 'js/src/libs/flags';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

const CODEO_GATED_SKILLS: ReadonlySet<string> = new Set([
    SKILL_NAMES.motionGraphicsScenesBasics,
    SKILL_NAMES.layeredGraphicsBasics,
    SKILL_NAMES.createMotionGraphicsVideo,
    SKILL_NAMES.createQuickMotionGraphicsVideo,
    SKILL_NAMES.createKineticTypographyVideo,
    SKILL_NAMES.htmlComposition,
    SKILL_NAMES.actOutTheLine,
]);

export function getDefaultSystemMessage(): string {
    return `You are a helpful video creation & editing assistant, part of Lumen5.

${getHowOurVideosWorkGuide('scene_id')}

${getVideoAnnotationGuide()}
`;
}

function getSkillsSystemMessageSection(skills: AnySkill[]): string {
    if (skills.length === 0) {
        return '';
    }
    const videoSkills = skills.filter((skill) => skill.kind === 'video');
    const howtoSkills = skills.filter((skill) => skill.kind === 'howto');
    const formatLines = (list: AnySkill[]): string =>
        list.map((skill) => `- ${skill.name}: ${skill.description}`).join('\n');

    const sections: string[] = [
        `
## Available Recipes & Skills
Recipes and skills are detailed instructions you load with the \`${TOOL_NAMES.loadSkill}\` tool.`,
    ];
    if (videoSkills.length > 0) {
        sections.push(`
**Recipes** — how to make a specific kind of video. When building a video, pick the best-matching recipe and load it BEFORE starting.

${formatLines(videoSkills)}`);
    }
    if (howtoSkills.length > 0) {
        sections.push(`
**Skills** — procedures for specific tasks. Load one whenever its situation comes up.

${formatLines(howtoSkills)}`);
    }
    return `${sections.join('\n')}\n`;
}

export type ToolboxRootAgentConfig = RootAgentConfig & {
    /** Skips the sketch-context load, five API calls a skill-gallery preview has no use for. */
    skipSketchContext?: boolean;
    maxConcurrentSubagents?: number;
    maxSubagentsPerMessage?: number;
};

export class ToolboxRootAgent extends RootAgent {
    private sketchContext: SketchAgentContextData = { ...EMPTY_SKETCH_AGENT_CONTEXT };
    private contextLoadingPromise: Promise<void>;
    // Assigned after super(), which builds the tools; they read it through a closure at call time.
    private readonly subagentRegistry: SubagentRegistry;

    // The base constructor builds tools/system message during super(), before
    // this field's initializer runs, so reads during construction get the
    // empty default rather than undefined.
    private get currentSketchContext(): SketchAgentContextData {
        return this.sketchContext ?? EMPTY_SKETCH_AGENT_CONTEXT;
    }

    override addBeforeAfterVideoDataDisplayMessages = true;

    constructor(config: ToolboxRootAgentConfig) {
        super({
            ...config,
            requiresVideoState: true,
            maxIterations: config.maxIterations ?? 200,
            streaming: config.streaming ?? true,
            enableThinkingSummaries: config.enableThinkingSummaries ?? true,
            // Off by default for the fastest responses.
            reasoningEffort: config.reasoningEffort ?? 'none',
            // The Toolbox reads and edits state by stable scene_id / scene_group_id handles.
            sceneAddressing: 'scene_id',
            // The Toolbox's model reads hex directly, so drop the `(name)` annotation on colors.
            includeColorNames: false,
        });

        this.subagentRegistry = new SubagentRegistry(
            config.maxConcurrentSubagents ?? DEFAULT_MAX_CONCURRENT_SUBAGENTS,
            config.maxSubagentsPerMessage ?? DEFAULT_MAX_SUBAGENTS_PER_MESSAGE,
        );

        this.contextLoadingPromise = config.skipSketchContext
            ? Promise.resolve()
            : loadSketchAgentContext(this.dispatch, this.getState).then((data) => {
                  this.sketchContext = data;
                  this.conversation.setSystemMessage(this.getSystemMessage());
                  if (!this.toolsOverride) {
                      this.conversation.setTools(
                          this.getTools(this.luminary, this.dispatch, this.getState),
                      );
                  }
              });
    }

    public getSubagentRegistry(): SubagentRegistry {
        return this.subagentRegistry;
    }

    /** A chart's `sceneIds` are the one scene its write tool is bound to. */
    public getSubagentTools(
        luminary: Luminary,
        dispatch: AppDispatch,
        getState: GetRootState,
        type: SubagentType,
        sceneIds: string[],
    ): Tool[] {
        // A chart writes its scene in one pass, so its one tool is the write, bound to that scene.
        if (type === 'chart') {
            return [new WriteSceneHtmlTool({ luminary, dispatch, getState, sceneId: sceneIds[0] })];
        }
        return this.buildTools(luminary, dispatch, getState, true).filter(
            (tool) => tool.subagentAccess !== SubagentAccess.UNAVAILABLE,
        );
    }

    /** A chart has its own prompt only; any other subagent gets this agent's, minus what it can't do. */
    public getSubagentSystemMessage(
        type: SubagentType,
        sceneIds: string[],
        luminary: Luminary,
        getState: GetRootState,
    ): string {
        return type === 'chart'
            ? getChartScenePrompt(luminary, getState(), sceneIds)
            : this.buildSystemMessage(true);
    }

    public override get shouldResumeConversation(): boolean {
        return true;
    }

    /** Resolves once the resumed conversation, templates, and voices are all loaded. */
    public override async waitForContext(): Promise<void> {
        await Promise.all([super.waitForContext(), this.contextLoadingPromise]);
    }

    private getSurface(): SkillSurface {
        return 'sketch';
    }

    public override getSkills(): AnySkill[] {
        return this.getSkillsFor(false);
    }

    private getSkillsFor(forSubagent: boolean): AnySkill[] {
        // A skill whose tools aren't registered for this team must not be offered. The codeo
        // skills sit behind the codeo flag, the chart-scenes skill behind the chart subagent flag,
        // and the avatar skill behind the avatar entitlement.
        const canUseCodeo = Flags.canUseCodeo();
        const canUseChartSubagent = Flags.canUseChartSubagent();
        const canUseAIAvatar = this.canUseAIAvatar();
        const skills = getActiveSkills(this.getSurface()).filter((skill) => {
            // A subagent can't start subagents, so any skill that tells it to is left out, read
            // from its text so a new one is covered without anyone having to list it.
            if (forSubagent && skill.instructions.includes(TOOL_NAMES.runSubagent)) {
                return false;
            }
            if (CODEO_GATED_SKILLS.has(skill.name) && !canUseCodeo) {
                return false;
            }
            if (skill.name === SKILL_NAMES.chartScenes && !canUseChartSubagent) {
                return false;
            }
            if (skill.name === SKILL_NAMES.aiAvatarVideoBasics && !canUseAIAvatar) {
                return false;
            }
            return true;
        });
        return skills.some((skill) => skill.name === SKILL_NAMES.chartScenes)
            ? skills
            : skills.map((skill) => withoutChartScenesPointer(skill));
    }

    /**
     * The ~80 drawing and motion techniques are loadable but absent from the system prompt — their
     * index would cost more than the whole preamble, so it rides inside the html-composition skill.
     */
    public override getLoadableSkills(): AnySkill[] {
        return this.getLoadableSkillsFor(false);
    }

    private getLoadableSkillsFor(forSubagent: boolean): AnySkill[] {
        const skills = this.getSkillsFor(forSubagent);
        return Flags.canUseCodeo() ? [...skills, ...getHtmlDesignSkills()] : skills;
    }

    private canUseAIVoiceover(): boolean {
        if (!this.getState) {
            return true;
        }
        try {
            return getCanUseAIVoiceover(this.getState());
        } catch {
            // State may not yet be fully populated (e.g. team settings still
            // loading); default to enabled rather than crashing the prompt.
            return true;
        }
    }

    /**
     * Whether the user's plan allows custom template colors (mirrors the editor's color gating).
     * Applying a named palette is always allowed; only set_template_color's arbitrary color needs
     * this. Defaults to disabled when the entitlement can't be read, so the plan-gated tool stays
     * hidden rather than exposed on an unresolved state.
     */
    private canUseColorPalettes(): boolean {
        try {
            return getIsUserAllowedAccessToColorPalettes(this.getState());
        } catch {
            return false;
        }
    }

    private canUseAIAvatar(): boolean {
        try {
            return getCanUseAIAvatar(this.getState());
        } catch {
            return false;
        }
    }

    private canPublishAIAvatar(): boolean {
        try {
            return getCanPublishAIAvatar(this.getState());
        } catch {
            return true;
        }
    }

    public override getSystemMessage(): string {
        return this.buildSystemMessage(false);
    }

    private buildSystemMessage(forSubagent: boolean): string {
        const base = `${getDefaultSystemMessage()}
${getSkillsSystemMessageSection(this.getSkillsFor(forSubagent))}`;
        const canUseVoiceover = this.canUseAIVoiceover();
        // Static guidance stays ahead of the toolbox sections, whose tail is
        // per-user/per-day content, to maximize the cacheable prompt prefix.
        return `${base}

${getToolboxPromptSections(this.currentSketchContext, {
    knowledgeCutoff: getKnowledgeCutoff(this.model),
    currentDate: getCurrentDate(),
    canUseAIVoiceover: canUseVoiceover,
    canPublishAIAvatar: this.canUseAIAvatar() ? this.canPublishAIAvatar() : true,
})}`;
    }

    override getTaskName(): string {
        return 'toolbox';
    }

    override get feedbackEnabled(): boolean {
        return true;
    }

    /**
     * A recipe the user picked on the Create page, which steers the one build it
     * was picked for. Consumed by the next message sent, so it never leaks into
     * the rest of the conversation the way a persisted control would.
     */
    private pendingRecipeName: string | null = null;

    public setPendingRecipeName(recipeName: string | null): void {
        this.pendingRecipeName = recipeName;
    }

    /** A past video picked on the Create page to match, consumed like the pending recipe. */
    private pendingReferenceVideo: SketchUserPreferencesOptions['referenceVideo'] = null;

    public setPendingReferenceVideo(
        referenceVideo: SketchUserPreferencesOptions['referenceVideo'],
    ): void {
        this.pendingReferenceVideo = referenceVideo;
    }

    /**
     * Set when the next message is a Script Composer handoff, which states its
     * own video settings, so the sketch-controls selections don't contradict it.
     * Consumed by that message, like the pending recipe.
     */
    private pendingFromScriptComposer = false;

    public setPendingFromScriptComposer(fromScriptComposer: boolean): void {
        this.pendingFromScriptComposer = fromScriptComposer;
    }

    protected override getUserMessageSuffix(): Promise<string> {
        const recipeName = this.pendingRecipeName;
        const referenceVideo = this.pendingReferenceVideo;
        const fromScriptComposer = this.pendingFromScriptComposer;
        this.pendingRecipeName = null;
        this.pendingReferenceVideo = null;
        this.pendingFromScriptComposer = false;
        return getSketchUserPreferences(this.getState, {
            blueprintViaSkill: true,
            recipeName,
            referenceVideo,
            fromScriptComposer,
        });
    }

    override getContextMessage() {
        return Promise.resolve(null);
    }

    private getCreationTools(): Tool[] {
        // Creation tools always use the blank sketch-context luminary.
        const shared = { luminary: this.luminary, dispatch: this.dispatch };
        // One video at a time, built directly on the editor luminary (which is
        // what `this.luminary` is here). create_blank_video only sets up the
        // blank video + its name; the DB video is created at turn end (an empty
        // video can't be persisted), after which autosave takes over.
        const tools: Tool[] = [
            new CreateBlankVideoTool({
                ...shared,
                getState: this.getState,
                getConversationMessages: () => this.conversation.getMessages(),
                getCurrentVideoId: () => getVideoId(this.getState()),
                onPrepareVideoCreation: (name) => {
                    this.dispatch(setVideoNameOperation(name));
                    // Show the editor's loading view immediately, before any
                    // scenes exist; cleared when the video is created at turn end.
                    this.dispatch(setToolboxCreatingVideo(true));
                },
            }),
            new RebuildVideoTool({
                ...shared,
                getState: this.getState,
                getCurrentVideoId: () => getVideoId(this.getState()),
            }),
        ];
        tools.push(new SuggestAnimatedExplainerTool(shared));
        return tools;
    }

    override getTools(luminary: Luminary, dispatch: AppDispatch, getState: GetRootState) {
        return this.buildTools(luminary, dispatch, getState, false);
    }

    private buildTools(
        luminary: Luminary,
        dispatch: AppDispatch,
        getState: GetRootState,
        forSubagent: boolean,
    ): Tool[] {
        const editTools: Tool[] = [
            new LoadSkillTool({
                luminary,
                dispatch,
                getState,
                skills: this.getLoadableSkillsFor(forSubagent),
                getCurrentVideoId: () => getVideoId(getState()),
            }),
            new AskUserQuestionTool({ luminary, dispatch }),
            new IconSearchTool({ luminary, dispatch, getState }),
            new SetIconTool({ luminary, dispatch, getState }),
            new SetAssetColorTool({ luminary, dispatch, getState }),
            new SetCaptionsTool({ luminary, dispatch, getState }),
            new SetAssetGroupTool({ luminary, dispatch, getState }),
            new MediaSearchTool({ luminary, dispatch, getState }),
            new DamMediaSearchTool({ luminary, dispatch, getState }),
            new ListMediaTool({ luminary, dispatch, getState }),
            new DocumentExtractTool({ luminary, dispatch, getState }),
            new GenerateAIMediaTool({ luminary, dispatch, getState }),
            new SetMediaTool({ luminary, dispatch, getState }),
            new EditMediaTool({ luminary, dispatch, getState }),
            new SetSceneDurationTool({ luminary, dispatch, getState }),
            new SetWatermarkTool({ luminary, dispatch, getState }),
            new SetWaveformStyleTool({ luminary, dispatch, getState }),
            new SetTextTool({ luminary, dispatch, getState }),
            new SetTextColorTool({ luminary, dispatch, getState }),
            new SetTextAnimationTool({ luminary, dispatch, getState }),
            new SetTextPositionPresetTool({ luminary, dispatch, getState }),
            new SetAspectRatioTool({ luminary, dispatch, getState }),
            new AddSceneTool({ luminary, dispatch, getState }),
            new AddSubsceneTool({ luminary, dispatch, getState }),
            // AddStickerTool is a known sketch bug, so it's omitted on the
            // sketch surface.
            new SearchVoicesTool({ luminary, dispatch, getState }),
            new AddAIVoiceoverSceneGroupTool({ luminary, dispatch, getState }),
            new AddTalkingHeadSceneGroupTool({ luminary, dispatch, getState }),
            new AddRecordedVoiceoverSceneGroupTool({ luminary, dispatch, getState }),
            new SearchUploadedVoiceoversTool({ luminary, dispatch }),
            new AddVoiceoverSceneTool({ luminary, dispatch, getState }),
            new EditAIVoiceoverTool({ luminary, dispatch, getState }),
            new FixPronunciationTool({ luminary, dispatch, getState }),
            new FetchReferenceVideoTool({ luminary, dispatch, getState }),
            new FindWaveformSceneDesignTool({ luminary, dispatch, getState }),
            new SplitSceneGroupTool({ luminary, dispatch, getState }),
            new TrimSceneGroupTool({ luminary, dispatch, getState }),
            new SetSceneGroupVolumeTool({ luminary, dispatch, getState }),
            new CleanUpRecordingTool({ luminary, dispatch, getState }),
            new ProposeClipsTool({ luminary, dispatch }),
            new MoveSceneGroupTool({ luminary, dispatch, getState }),
            new GetVideoStateTool({ luminary, dispatch, getState }),
            new LintVideoTool({ luminary, dispatch, getState, sceneAddressing: 'scene_id' }),
            new DeleteSceneTool({ luminary, dispatch, getState }),
            new DeleteSceneGroupTool({ luminary, dispatch, getState }),
            new SetSceneRangeTool({ luminary, dispatch, getState }),
            new ListSceneDesignTool({ luminary, dispatch, getState }),
            new FindTemplatesTool({ luminary, dispatch, getState }),
            new AnalyzeVideoTool({ luminary, dispatch, getState }),
            new AnalyzeClipFramingTool({ luminary, dispatch, getState }),
            new ImageAskTool({ luminary, dispatch, getState }),
            new ReviewSceneImagesTool({ luminary, dispatch, getState }),
            new SetMusicTool({ luminary, dispatch }),
            new SetMusicVolumeTool({ luminary, dispatch }),
            new MusicSearchTool({ luminary, dispatch }),
            new SetGlobalColorGradingTool({
                luminary,
                dispatch,
                getState,
                colorGradingFilters: this.currentSketchContext.colorGradingFilters,
            }),
            new SetMediaEffectsTool({
                luminary,
                dispatch,
                getState,
                colorGradingFilters: this.currentSketchContext.colorGradingFilters,
            }),
            new SetColorPaletteTool({ luminary, dispatch, getState }),
            new UrlFetchTool({ luminary, dispatch }),
            new SketchResearchTool({ luminary, dispatch }),
            new SuggestUpgradeTool({ luminary, dispatch }),
            new ListBrandKitsTool({ luminary, dispatch, getState }),
            new ApplyBrandKitTool({ luminary, dispatch, getState }),
            new ExtractBrandTool({ luminary, dispatch, getState }),
            new ToolboxSaveBrandKitTool({ luminary, dispatch, getState }),
            new SetSceneTransitionTool({ luminary, dispatch, getState }),
            new TranslateVideoTool({ luminary, dispatch, getState }),
        ];
        editTools.push(
            new RunSubagentTool({
                luminary,
                dispatch,
                getState,
                getRegistry: () => this.subagentRegistry,
                allowedTypes: getAllowedSubagentTypes(),
                buildSubagentTools: (type, sceneIds) =>
                    this.getSubagentTools(luminary, dispatch, getState, type, sceneIds),
                getSubagentSystemMessage: (type, sceneIds) =>
                    this.getSubagentSystemMessage(type, sceneIds, luminary, getState),
            }),
        );
        // Setting an arbitrary custom color is plan-gated the same way the editor gates it; applying
        // a named palette (SetColorPaletteTool above) is always allowed.
        if (this.canUseColorPalettes()) {
            editTools.push(new SetTemplateColorTool({ luminary, dispatch, getState }));
        }
        if (this.canUseAIAvatar()) {
            editTools.push(new ListAvatarsTool({ luminary, dispatch, getState }));
            editTools.push(new AddAIAvatarSceneGroupTool({ luminary, dispatch, getState }));
        }
        if (Flags.canUseCodeo()) {
            editTools.push(
                ...buildDesignAuthoringTools(luminary, dispatch, getState, () =>
                    this.conversation.getMessagesForAPI({ reportTruncation: false }),
                ),
            );
        }
        return [...this.getCreationTools(), ...editTools];
    }
}
